import { Search } from '@mui/icons-material'
import { observer } from 'mobx-react-lite'
import { ReactElement, useEffect, useState } from 'react'
import { I18n, Translate } from 'common'
import { useUiStateStore } from '../../../../../src/store/uiState/UiStateStore'
import { Button } from '../../../../../src/ui-components/forms/Button'
import styled from 'styled-components'
import { Checkbox } from '../../../../../src/ui-components/forms/Checkbox'
import { LinesManufacturer } from '../../../../../src/store/models/SearchModels'
import { useResultsStore } from '../../store/ResultsStore'

type LinesManufacturers = Array<LinesManufacturer>

export interface SelectManufaturersTableProps {
  linesManufacturersData: LinesManufacturers
  onHandleDrawer?: () => void
}

export const SelectManufaturersTable = observer(
  (props: SelectManufaturersTableProps): ReactElement => {
    const { linesManufacturersData } = props

    const [listData, setListData] = useState<LinesManufacturers>(
      linesManufacturersData
    )

    const { selectedLinesManufacturers, setSelectedLinesManufacturers } =
      useResultsStore()
    const { setShowFilterLinesDrawer } = useUiStateStore()

    const [searchInputValue, setSearchInputValue] = useState('')

    const [selectedItems, setSelectedItems] = useState<string[]>([])

    const handleSearch = () => {
      if (searchInputValue === '') {
        setListData(linesManufacturersData)
      } else {
        setListData(
          linesManufacturersData.filter((item) =>
            item.name.toLowerCase().includes(searchInputValue.toLowerCase())
          )
        )
      }
    }

    const handleManufacturerSelection = (checkedObj) => {
      if (selectedItems.length === 10 && checkedObj.isChecked) {
        return
      }

      const obj = linesManufacturersData.find(
        (item) => item.name === checkedObj.label
      )

      if (checkedObj.isChecked) {
        setSelectedItems((prevData) => [...prevData, obj.name])
      } else {
        setSelectedItems(selectedItems.filter((item) => item !== obj.name))
      }
    }

    useEffect(() => {
      if (selectedLinesManufacturers.length > 0) {
        setSelectedItems(selectedLinesManufacturers)
      }
    }, [selectedLinesManufacturers])

    const manufacturers = `${Translate('manufacturers')}(${
      selectedItems.length
    }/10)`

    const tableHead = (
      <TableHead>
        <tr>
          <TableHeadColumn align="left" width="40%">
            <I18n name={manufacturers} />
          </TableHeadColumn>
          <TableHeadColumn align="right" width="60%">
            <I18n name="lineCode" />
          </TableHeadColumn>
          <TableHeadColumn align="right" width="20%">
            <I18n name="cat" />
          </TableHeadColumn>
        </tr>
      </TableHead>
    )

    return (
      <Wrapper>
        <SearchWrapper>
          <SearchInput
            value={searchInputValue}
            onChange={(e) => setSearchInputValue(e.target.value)}
            placeholder={Translate('searchManufacturers')}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSearch()
              }
            }}
          />
          <Search
            style={{ marginRight: 20, fontSize: 20, cursor: 'pointer' }}
            onClick={handleSearch}
          />
        </SearchWrapper>

        <Table>
          {tableHead}
          <TableBody>
            {listData.map((listItem) => (
              <TableRow key={listItem.name}>
                <RowWrapper align="left">
                  <Checkbox
                    label={listItem.name}
                    value={listItem.name}
                    active={Boolean(
                      selectedItems.find((item) => item === listItem.name)
                    )}
                    onClick={handleManufacturerSelection}
                  />
                </RowWrapper>
                <RowWrapper align="center">{listItem.userLinecode}</RowWrapper>
                <RowWrapper align="center">
                  {listItem.catalogLineCode}
                </RowWrapper>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <ButtonsWrapper>
          {selectedItems.length !== 0 ? (
            <Button
              onClick={() => {
                setSelectedItems([])
              }}
              variant="secondary"
              size="medium"
              text={Translate('clearAll')}
            />
          ) : null}

          <Button
            onClick={() => {
              setSelectedLinesManufacturers(selectedItems)
              setShowFilterLinesDrawer(false)
            }}
            variant="primary"
            size="medium"
            text={Translate('applyChanges')}
            disabled={selectedItems.length === 0}
          />
        </ButtonsWrapper>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  width: '100%';
`

const SearchWrapper = styled.div`
  background: #ffffff;
  border: 1px solid #d3d2cf;
  box-sizing: border-box;
  border-radius: 0 3px 3px 0;
  height: 48px;
  flex-grow: 1;
  width: '100%';
  display: flex;
  flex-direction: row;
  align-items: center;
  position: relative;
  margin-top: 10px;
`

const SearchInput = styled.input`
  border: none;
  width: 99%;
  height: 100%;
  margin-left: 16px;
  flex-grow: 1;
  overflow: hidden;
  &:focus {
    outline-width: 0;
    outline: none;
  }
  &::placeholder {
    color: #9b9b9b;
    font-family: Roboto, serif;
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 24px;
    text-overflow: ellipsis;
  }
`
const Table = styled.table`
  width: 100%;
  margin-top: 20px;
  display: block;
  height: 400px;
  overflow-y: scroll;
  overflow-x: hidden;
  border-collapse: collapse;
`
const TableHead = styled.thead`
  background-color: #000;
  color: #fff;
`
interface TableHeadColumnProps {
  width: string
}
const TableHeadColumn = styled.td<TableHeadColumnProps>`
  font-family: Oswald, serif;
  font-weight: bold;
  font-size: 16px;
  padding: 10px 25px;
  text-align: ${(p) => (p.align ? p.align : 'center')};
  width: ${(p) => p.width};
`

const TableRow = styled.tr`
  border-bottom: 1pt solid silver;
`

const RowWrapper = styled.td`
  font-family: Roboto;
  font-weight: normal;
  font-size: 16px;
  vertical-align: middle;
  padding: 10px 0;
  text-align: ${(p) => (p.align ? p.align : 'center')};
`

const TableBody = styled.tbody``

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 20px;
`
